import { LuPlus } from "react-icons/lu";
import { useMemo, useState, type FC } from "react";
import {
  Button,
  ButtonProps,
  DialogTriggerProps,
  IconButton,
  Separator,
  useBreakpointValue,
} from "@chakra-ui/react";

import {
  DialogBody,
  DialogRoot,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  DialogContent,
  DialogBackdrop,
  DialogCloseTrigger,
} from "@/components/ui/dialog";
import { CreateOrderForm } from "./create-order-form";
import { CreateOrderLinesListing } from "./create-order-lines-listing";
import { CreateOrderSubmissionControls } from "./create-order-submission-controls";

import { SectionHeader } from "@shared/ui/section-header";
import type { InferProps } from "@shared/types/misc";
import { Can } from "@casl/react";
import { useAbilities } from "@/app/access-control";

interface Props {
  buttonProps?: Partial<ButtonProps>;
  triggerProps?: Partial<DialogTriggerProps>;
  formProps?: InferProps<typeof CreateOrderForm>;
  onCompleted?: () => void;
  isCompact?: boolean;
}

export const CreateOrderModal: FC<Props> = ({
  formProps,
  isCompact,
  triggerProps,
  buttonProps,
  onCompleted,
}) => {
  const abilities = useAbilities();
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: true });
  const [isOpen, setIsOpen] = useState<boolean>();

  const ButtonComponent = useMemo(
    () => (isCompact ? IconButton : Button),
    [isCompact]
  );

  return (
    <Can I="create" a="preorder" ability={abilities}>
      <DialogRoot
        size={isMobile ? "full" : "xl"}
        open={isOpen}
        onOpenChange={(e) => setIsOpen(e.open)}
      >
        <DialogBackdrop />
        <DialogTrigger {...triggerProps}>
          <ButtonComponent
            size={{ base: "xs", xl: "md" }}
            variant="solid"
            onClick={() => setIsOpen(true)}
            {...buttonProps}
          >
            <LuPlus /> {!isCompact && "Create new order"}
          </ButtonComponent>
        </DialogTrigger>
        <DialogContent>
          <DialogCloseTrigger />
          <DialogHeader>
            <SectionHeader
              icon={LuPlus}
              title="Create new order"
              leadingIconProps={{ containerProps: { p: 1 } }}
            />
          </DialogHeader>
          <DialogBody>
            <CreateOrderForm {...formProps} />
            <Separator w="full" my="4" />
            <CreateOrderLinesListing />
          </DialogBody>
          <DialogFooter>
            <CreateOrderSubmissionControls
              onClose={() => setIsOpen(false)}
              onSubmitted={onCompleted}
            />
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Can>
  );
};
